import React, { useCallback, useMemo, useState } from 'react';
import { Icon, Button, Modal, InputWrapper, Input } from '@components';
import { Category, Product, UpdateProductFormType } from '@utilities/types';
import { getProductSellingPrice, getProductPurchasePrice } from '@utilities/functions';

interface ProductCardProps {
  product: Product;
  categories: Category[]
  onViewDetails: () => void;
  onPricingEvolution: () => void;
  onEdit: (productId: string, product: UpdateProductFormType) => void;
  onInventory: () => void;
  onSupply: () => void;
  onPromotion: () => void;
}

export const ProductCard: React.FC<ProductCardProps> = ({
  product,
  categories,
  onViewDetails,
  onPricingEvolution,
  onEdit,
  onInventory,
  onSupply,
  onPromotion,
}) => {

    const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
    const [productCategory, setProductCategory] = useState<string>(product._categoryID._id);
    const [productName, setProductName] = useState<string>(product.name);
    const [productDescription, setProductDescription] = useState<string>(product.description);
    const [productPrice, setProductPrice] = useState<string>(String(getProductSellingPrice(product)));
    const [productPurchasePrice, setProductPurchasePrice] = useState<string>(String(getProductPurchasePrice(product)));

    const categoriesOptions = useMemo(() => {
        return categories.map(category => ({
            label: category.name,
            value: category._id,
        }));
    }, [categories]);

    const images = useMemo(()=> {
        const tmp = product.images.map(img=> `${process.env.REACT_APP_IMAGE_BASE_URL}/${img.fileDir}/${img.filename}`)
        return tmp.length > 0 ? tmp : [];
    }, [product]);

    const price = useMemo(()=> (
        getProductSellingPrice(product)
    ), [product]);

    const purchasePrice = useMemo(()=> (
        getProductPurchasePrice(product)
    ), [product]);

    const discountedPrice = useMemo(()=> {
        if(product._promotionID){
            const discount = product._promotionID.type === 'percentage' ? price * product._promotionID.value / 100 : product._promotionID.value;
            const discountedPrice = price - discount;
            return discountedPrice;
        } else {
            return null;
        }
    }, [price, product]);

    const isActive = useMemo(() => product.isActive, [product]);

    const isValidCategorySelected = useMemo(()=> (product._categoryID._id !== productCategory), [product._categoryID._id, productCategory]);
    const isValidName = useMemo(()=> (productName !== '' && product.name !== productName), [product.name, productName]);
    const isValidDescription = useMemo(()=> (productDescription !== '' && product.description !== productDescription), [product.description, productDescription]);
    const isValidPrice = useMemo(()=> (productPrice !== '' && price !== Number(productPrice)), [price, productPrice]);
    const isValidPurchasePrice = useMemo(()=> (productPurchasePrice !== '' && purchasePrice !== Number(productPurchasePrice)), [purchasePrice, productPurchasePrice]);

    const isEnableButton = useMemo(() => {
        return isValidCategorySelected || isValidName || isValidDescription || isValidPrice || isValidPurchasePrice;
    }, [isValidCategorySelected, isValidName, isValidDescription, isValidPrice, isValidPurchasePrice]);

    const onToggleModalEdit = useCallback(() => {
        setEditModalOpen(!editModalOpen);
    }, [editModalOpen]);

    const onToggle = useCallback(() => {
        onEdit(product._id!, {
            ...product,
            isActive: !isActive,
        });
    }, [isActive, product, onEdit]);

    const handleEdit = useCallback(() => {
        if(!isEnableButton) return;
        onEdit(product._id!, {
            ...(isValidCategorySelected && { categoryId: productCategory }),
            ...(isValidName && { name: productName }),
            ...(isValidDescription && { description: productDescription }),
            ...(isValidPrice && { price: productPrice }),
            ...(isValidPurchasePrice && { purchasePrice: productPurchasePrice }),
        });
        onToggleModalEdit();
    }, [
        isEnableButton, product, isValidCategorySelected, 
        productCategory, isValidName, productName, 
        isValidDescription, productDescription, 
        isValidPrice, productPrice, isValidPurchasePrice,
        productPurchasePrice, onEdit, onToggleModalEdit
    ]);

    return (
        <>
            <div className="card bg-white shadow-md rounded-lg overflow-hidden w-full max-w-sm">
                <div className="relative w-full h-48 overflow-hidden">
                    <div className="carousel w-full h-full">
                    {images.map((img, index) => (
                        <div key={index} className="carousel-item w-full">
                        <img
                            src={img}
                            alt={`Produit ${index}`}
                            className="w-full h-48 object-cover"
                        />
                        </div>
                    ))}
                    </div>
                </div>

                <div className="p-4 space-y-2">
                    <h2 className="text-lg font-semibold">{product.name}</h2>

                    <div className="flex items-center gap-2">
                    {discountedPrice ? (
                        <>
                        <span className="text-gray-400 line-through text-sm">
                            {price} Fcfa
                        </span>
                        <span className="text-primary font-bold">
                            {discountedPrice.toLocaleString()} Fcfa
                        </span>
                        </>
                    ) : (
                        <span className="text-primary font-bold">{price} Fcfa</span>
                    )}
                    </div>

                    <div className="flex items-center justify-end gap-1">
                        <span className="text-sm">{isActive ? 'Actif' : 'Inactif'}</span>
                        <input
                            type="checkbox"
                            className="toggle toggle-primary"
                            checked={isActive}
                            onChange={onToggle}
                        />
                    </div>

                    <div className="flex flex-wrap gap-2">
                        <Button 
                            onClick={onToggleModalEdit}
                            variant='accent'
                            className='btn-sm'
                        >
                            <Icon 
                                iconName="Pencil" size={18} 
                            /> Modifier
                        </Button>
                        <Button 
                            onClick={onPricingEvolution}
                            variant='primary'
                            className='btn-sm'
                        >
                            <Icon 
                                iconName="CircleDollarSign" size={18} 
                            /> Evolution du prix
                        </Button>
                        <Button 
                            onClick={onSupply} 
                            variant='secondary'
                            className='btn-sm'
                        >
                            <Icon 
                                iconName="Truck" size={18}
                            /> Approvisionnement
                        </Button>
                        <Button 
                            onClick={onInventory} 
                            variant='info'
                            className='btn-sm'
                        >
                            <Icon 
                                iconName="ClipboardList" size={18}
                            /> Inventaire
                        </Button>
                        <Button
                            onClick={onPromotion}
                            variant='warning'
                            className='btn-sm'
                            disabled
                        >
                            <Icon 
                                iconName="Percent" size={18} 
                            /> Promotion
                        </Button>
                    </div>
                    
                    <Button onClick={onViewDetails} className="btn btn-primary btn-sm w-full">
                        Voir le produit
                    </Button>
                </div>
            </div>

            <Modal
                isOpen={editModalOpen}
                onClose={onToggleModalEdit}
                title="Modifier le produit"
            >
                <div className='space-y-4'>
                    <InputWrapper label='Catégorie du produit'>
                        <Input 
                            placeholder='ex: Vêtements'
                            type='select'
                            value={productCategory}
                            onChange={(e) => setProductCategory(e.target.value)}
                            options={categoriesOptions}
                        />
                    </InputWrapper>

                    <InputWrapper label='Nom du produit'>
                        <Input 
                            placeholder='ex: T-shirt'
                            value={productName}
                            onChange={(e) => setProductName(e.target.value)}
                        />
                    </InputWrapper>

                    <InputWrapper label='Prix de vente'>
                        <Input 
                            placeholder='ex: 1000'
                            type='number'
                            value={productPrice}
                            onChange={(e) => setProductPrice(e.target.value)}
                        />
                    </InputWrapper>

                    <InputWrapper label='Prix d&#39;achat (optionnel)'>
                        <Input 
                            placeholder='ex: 800'
                            type='number'
                            value={productPurchasePrice}
                            onChange={(e) => setProductPurchasePrice(e.target.value)}
                        />
                    </InputWrapper>

                    <InputWrapper label='Description du produit'>
                        <Input 
                            placeholder='ex: T-shirt en coton'
                            type='textarea'
                            value={productDescription}
                            onChange={(e) => setProductDescription(e.target.value)}
                        />
                    </InputWrapper>

                    <div className="flex justify-between gap-2">
                        <Button
                            className='bg-neutral'
                            onClick={onToggleModalEdit}
                        >
                            Annuler
                        </Button>

                        <Button
                            variant='success'
                            onClick={handleEdit}
                            disabled={!isEnableButton}
                        >
                            Modifier
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    );
};
